import type { QueryClient } from "@tanstack/react-query";
import type { ModelName } from "../types";
import { buildTellescopeQueryKey } from "../keys";
import { TellescopeModelFromName } from "../mappings";

/**
 * The result of a partial cache lookup for a batch of IDs.
 *
 * `cached` contains items whose `getOne` cache entries are still fresh.
 * `missingIds` contains IDs that were absent or stale and must be fetched.
 * `oldestDataUpdatedAt` is the earliest `dataUpdatedAt` timestamp among all
 * cache hits, which can be used to set the composite query's `dataUpdatedAt`.
 */
interface PartialCacheResolution<Model extends ModelName> {
  cached: Array<TellescopeModelFromName<Model>>;
  missingIds: string[];
  oldestDataUpdatedAt: number | undefined;
}

/**
 * Splits a list of IDs into those already in the `getOne` cache (and within
 * the allowed stale window) and those that still need to be fetched.
 *
 * Call this before issuing a `getByIds` request to avoid re-fetching data that
 * was recently loaded via `getOne` queries.
 *
 * @param queryClient - The TanStack Query client holding the cache to inspect.
 * @param model - The Tellescope model name (e.g. `"endusers"`).
 * @param ids - The full list of IDs requested by the caller.
 * @param staleTime - Maximum age in milliseconds for a cache entry to be
 *   considered fresh. Defaults to `0`, meaning only entries updated this tick
 *   are reused.
 * @returns A `PartialCacheResolution` with hit items, IDs still needed, and
 *   the oldest cache timestamp among the hits.
 */
export const resolveGetByIdsFromCache = <Model extends ModelName>({
  queryClient,
  model,
  ids,
  staleTime = 0,
}: {
  queryClient: QueryClient;
  model: Model;
  ids: string[];
  staleTime?: number;
}): PartialCacheResolution<Model> => {
  const cached: Array<TellescopeModelFromName<Model>> = [];
  const missingIds: string[] = [];
  let oldestDataUpdatedAt: number | undefined;

  for (const id of ids) {
    const queryKey = buildTellescopeQueryKey<Model, "getOne">({
      model,
      method: "getOne",
      // @ts-expect-error All models have an id, but TS doesn't know that here
      request: { id },
    });

    const state = queryClient.getQueryState(queryKey);
    const data = queryClient.getQueryData<TellescopeModelFromName<Model>>(queryKey);

    if (data && state) {
      const age = Date.now() - state.dataUpdatedAt;
      if (age <= staleTime) {
        cached.push(data);
        if (oldestDataUpdatedAt === undefined || state.dataUpdatedAt < oldestDataUpdatedAt) {
          oldestDataUpdatedAt = state.dataUpdatedAt;
        }
        continue;
      }
    }

    missingIds.push(id);
  }

  return { cached, missingIds, oldestDataUpdatedAt };
};
